import React from "react";
import { toNumber } from "lodash";
import { useTranslation } from "react-i18next";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { AccessLevel } from "@/shared/constants";
import { ResourceEnum } from "@/shared/constants/resources";
import { useOrganisation } from "@/features/organization/hooks/useOrganisation";
import { usePositions } from "@/features/projects/hooks/usePositions";
import { useTeams } from "@/features/teams/hooks/useTeams";

interface RequireOrgListAccessProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Org LIMITED is container visibility only. If a viewer has org LIMITED but has no
 * explicit access to any teams/projects, we want to show the NoRights banner.
 *
 * Org EDIT/FULL users should still be able to see normal empty states.
 */
function getOrgAccess({
  validateAccessToResource,
}: {
  validateAccessToResource: ReturnType<
    typeof useViewerPermissions
  >["validateAccessToResource"];
}) {
  const hasOrgLimited = validateAccessToResource({
    resourceType: ResourceEnum.organization,
    accessLevel: AccessLevel.limited,
  });
  const hasOrgEdit = validateAccessToResource({
    resourceType: ResourceEnum.organization,
    accessLevel: AccessLevel.edit,
  });

  return { hasOrgLimited, hasOrgEdit };
}

export function RequireProjectsListAccess({
  children,
  className = "overflow-y-auto",
}: RequireOrgListAccessProps) {
  const { t } = useTranslation();
  const { organization, loading: orgLoading } = useOrganisation();
  const orgId = toNumber(organization?.id);

  const { validateAccessToResource, permissionsLoading } =
    useViewerPermissions();
  const { hasOrgLimited, hasOrgEdit } = getOrgAccess({
    validateAccessToResource,
  });

  const { positions, loading: projectsLoading } = usePositions(orgId);

  const loading = orgLoading || permissionsLoading || projectsLoading;
  const permissionLocked =
    !hasOrgLimited || (hasOrgLimited && !hasOrgEdit && positions.length === 0);

  if (loading)
    return (
      <ScreenContainer
        loading
        loadingMessage={t("common.loading_projects")}
        className={className}
      />
    );
  if (permissionLocked)
    return <ScreenContainer permissionLocked className={className} />;

  return <>{children}</>;
}

export function RequireTeamsListAccess({
  children,
  className = "overflow-y-auto",
}: RequireOrgListAccessProps) {
  const { t } = useTranslation();
  const { organization, loading: orgLoading } = useOrganisation();
  const orgId = toNumber(organization?.id);

  const { validateAccessToResource, permissionsLoading } =
    useViewerPermissions();
  const { hasOrgLimited, hasOrgEdit } = getOrgAccess({
    validateAccessToResource,
  });

  const { teams, loading: teamsLoading } = useTeams(orgId);

  const loading = orgLoading || permissionsLoading || teamsLoading;
  const permissionLocked =
    !hasOrgLimited || (hasOrgLimited && !hasOrgEdit && teams.length === 0);

  if (loading)
    return (
      <ScreenContainer
        loading
        loadingMessage={t("common.loading_teams")}
        className={className}
      />
    );
  if (permissionLocked)
    return <ScreenContainer permissionLocked className={className} />;

  return <>{children}</>;
}
